import React, { JSX, useCallback, useMemo } from 'react';
import { Column } from 'react-table';
import { CopyIcon } from '@chakra-ui/icons';
import { Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { Modal, ModalType, Table, useToast } from 'common/components';

import { getLocalDateTimeByUtcTimestamp } from '../../../../common/utils';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { PaymentHistoryResponse } from '../model/payment-history.response';
import { mapPaymentMethodTypeToLabel } from '../utils/eco-fee.helper';
import { PaymentStatus } from './PaymentStatus';

export type DebtPaymentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  payments: PaymentHistoryResponse[];
};

export const DebtPaymentModal = ({ isOpen, onClose, payments }: DebtPaymentModalProps): JSX.Element => {
  const t = useTranslations();
  const { displayToast } = useToast();

  const copyPspIdToClipboard = useCallback(
    (original: PaymentHistoryResponse) => () => {
      navigator.clipboard.writeText(original.pspId as string).then(() => {
        displayToast('success', t('users_eco_fee.payments.toast.psp_id_copied'));
      });
    },
    [displayToast, t],
  );

  const modalColumns: Column<PaymentHistoryResponse>[] = useMemo(
    () => [
      {
        id: 'transactionId',
        Header: t('users_eco_fee.payments.table.payment_id'),
        accessor: 'transactionId' as const,
      },
      {
        id: 'status',
        Header: t('users_eco_fee.payments.table.status'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => <PaymentStatus payment={original} />,
      },
      {
        id: 'transactionTimestamp',
        Header: t('users_eco_fee.payments.table.charged_at'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => <>{getLocalDateTimeByUtcTimestamp(original.transactionTimestamp)}</>,
      },
      {
        id: 'paymentMethod',
        Header: t('users_eco_fee.payments.table.payment_method'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => <>{original.paymentMethod ? mapPaymentMethodTypeToLabel(original.paymentMethod.type, t) : '-'}</>,
      },
      {
        id: 'pspId',
        Header: t('users_eco_fee.payments.table.psp_id'),
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Flex align="center" gap={2} maxW="380px">
            <Text whiteSpace="normal" wordBreak="break-all" overflowWrap="anywhere" flex="1 1 auto">
              {original.pspId ?? '-'}
            </Text>
            {!!original.pspId && (
              <Tooltip label={t('users_eco_fee.payments.toast.psp_id_copy')} placement="top">
                <IconButton
                  aria-label={t('users_eco_fee.payments.toast.psp_id_copied')}
                  icon={<CopyIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={copyPspIdToClipboard(original)}
                />
              </Tooltip>
            )}
          </Flex>
        ),
      },
    ],
    [copyPspIdToClipboard, t],
  );

  return (
    <Modal
      isModalVisible={isOpen}
      onModalClose={onClose}
      onConfirmClick={onClose}
      modalHeader={t('users_eco_fee.payments.title')}
      size={'4xl'}
      type={ModalType.INFO}
    >
      <Table<PaymentHistoryResponse> data={payments} columns={modalColumns} isLoading={false} />
    </Modal>
  );
};
